import clsx from 'clsx';
import { GatsbyImage } from 'gatsby-plugin-image';
import { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';

import { LocaleNavbarBasicLink, LocaleNavbarTab } from 'typescriptDefs/layout';

import { FeaturedLink } from './FeaturedLink';
import { NavDescription, NavLink, SectionTitle } from './NavTypography';

type Props = {
  darkBg: boolean;
  tab: LocaleNavbarTab;
  trackLinkClicked: (label: string) => void;
};

export const DesktopMenu = ({ darkBg, tab, trackLinkClicked }: Props) => {
  return (
    <div
      id="menu-dropdown"
      className={twMerge(
        clsx(
          'absolute top-navMobileHeight z-nav hidden h-fit w-full bg-white [box-shadow:0px_4px_4px_#00000003] lg:top-navFullMinimizedHeight lg:block',
          darkBg && 'bg-primary-500 text-white [box-shadow:0px_4px_4px_#0000001A]',
        ),
      )}
    >
      <div className="h-fit w-full pb-64 pt-32">
        <Container className="flex">
          <div
            className="grid w-fit gap-x-48 gap-y-16 px-16 [grid-template-columns:repeat(var(--num-cols),minmax(0,1fr))] [grid-template-rows:min-content] xs:px-0"
            style={{ '--num-cols': tab.content.length } as CSSProperties}
          >
            {tab.content.map((section) =>
              section._type === 'siteNavSection' ? (
                <div
                  key={`${section.title}-title`}
                  className={twMerge(clsx('flex gap-16', section.alignVertically && 'max-w-180'))}
                >
                  <SectionTitle darkBg={darkBg}>{section.title}</SectionTitle>
                  {!section.alignVertically && (
                    <div className="flex flex-col gap-28">
                      {section.description && <NavDescription darkBg={darkBg}>{section.description}</NavDescription>}
                      <SectionLinks
                        key={`${section.title}-content`}
                        className="lg:mt-auto"
                        darkBg={darkBg}
                        links={section.links}
                        trackLinkClicked={trackLinkClicked}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  className="leading-none"
                  key={`${section.title}-title`}
                  title
                  darkBg={darkBg}
                  item={section}
                  onClick={() => trackLinkClicked(section.title)}
                />
              ),
            )}
            {tab.content.map((section) => {
              if (!section.description) return null;
              if (section._type === 'siteNavSection') {
                return section.alignVertically ? (
                  <NavDescription darkBg={darkBg} key={`${section.title}-description`}>
                    {section.description}
                  </NavDescription>
                ) : null;
              }
              return (
                <NavDescription darkBg={darkBg} key={`${section.title}-description`}>
                  {section.description}
                </NavDescription>
              );
            })}
            {tab.content.map((section) =>
              section._type === 'siteNavSection' ? (
                section.alignVertically && (
                  <SectionLinks
                    key={`${section.title}-content`}
                    darkBg={darkBg}
                    links={section.links}
                    trackLinkClicked={trackLinkClicked}
                  />
                )
              ) : (
                <div key={`${section.title}-content`} className="h-full max-w-180">
                  {!!section.image && (
                    <GatsbyImage
                      className="mt-auto h-80 w-180 rounded-8"
                      image={section.image.img.asset.gatsbyImageData}
                      alt={section.image.altText}
                      role="presentation"
                    />
                  )}
                </div>
              ),
            )}
          </div>
          {!!tab.featured && (
            <FeaturedLink darkBg={darkBg} featured={tab.featured} trackLinkClicked={trackLinkClicked} />
          )}
        </Container>
      </div>
    </div>
  );
};

const SectionLinks = ({
  className,
  darkBg,
  links,
  trackLinkClicked,
}: {
  className?: string;
  darkBg: boolean;
  links: LocaleNavbarBasicLink[];
  trackLinkClicked: (title: string) => void;
}) => {
  return (
    <div className={twMerge('flex max-w-180 flex-col gap-16', className)}>
      {links.map((item) => (
        <div key={item.title}>
          <NavLink key={item.title} item={item} darkBg={darkBg} onClick={() => trackLinkClicked(item.title)} />
        </div>
      ))}
    </div>
  );
};
